/**
 * Visor de contenidos del curso (Alianza Francesa).
 *
 * - Pestaña « Nomade » : embebe los episodios Genially (view.genially.com) de
 *   cada temporada del parcours numérico. El estudiante puede ver el episodio y
 *   conversar con Galia sobre él sin salir de la aplicación.
 * - Pestaña « Cours internes » : catálogo de la méthode « Mon alter ego »
 *   (libros MAE 1-3) usada en los cursos presenciales, con la asociación
 *   libro ↔ nivel ↔ módulos AF y sus objetivos, más los recursos del libro
 *   (Classe+, guías, tests). Visible para estudiantes y profesores.
 *
 * Catálogo: src/config/afContent.ts (derivado de los Excel de la AF).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  NMD_SEASONS_AVAILABLE,
  nmdSeasonsForStudent,
  MAE_BOOKS,
  MAE_RESOURCES,
  geniallyViewUrl,
  type NmdSeason,
  type NmdEpisode,
  type MaeBook,
} from '../../config/afContent';
import configService from '../../services/configService';
import sessionService from '../../services/sessionService';
import type { MCERLevel } from '../../types';
import './ContentViewer.css';

interface ContentViewerProps {
  /** Nivel configurado del usuario: preselecciona la temporada/libro de su nivel. */
  mcerLevel?: MCERLevel;
  /** Mostrar la pestaña « Cours internes » (Mon alter ego). */
  showCoursInternes?: boolean;
  /** Si es profesor: ve también el material docente (guías, tests, corrigés).
   *  Los estudiantes solo ven el material apto para estudiantes. */
  isTeacher?: boolean;
  /** Modo profesor (aide-prof): si se provee, muestra el botón « Co-construire
   *  avec Galia » que lleva el episodio/libro que el profesor está viendo al chat
   *  como un mensaje, para diseñar actividades a partir de ese contenido. */
  onWorkWithContent?: (message: string) => void;
  onClose: () => void;
  /**
   * Visor EN COLUMNA, no modal: sin fondo oscuro ni botón de cerrar. Lo usa el
   * layout «Espace de travail» (rediseño opción 1a): el contenido vive en una
   * columna fija junto al chat y nunca lo tapa. Por defecto, modal (como hoy).
   */
  inline?: boolean;
}

type ViewerTab = 'nomade' | 'cours';

function defaultSeasonFor(level: MCERLevel | undefined, seasons: NmdSeason[]): NmdSeason {
  if (!level) return seasons[0];
  // Sin temporada de su nivel exacto (p. ej. B2+): la más avanzada que puede ver.
  return seasons.find((s) => s.level === level) ?? seasons[seasons.length - 1];
}

function defaultBookFor(level?: MCERLevel): MaeBook {
  const match = level
    ? MAE_BOOKS.find((b) => b.levels.includes(level))
    : undefined;
  return match ?? MAE_BOOKS[0];
}

export function ContentViewer({ mcerLevel, showCoursInternes = false, isTeacher = false, onWorkWithContent, onClose, inline = false }: ContentViewerProps) {
  // Modo profesor (aide-prof): el botón « Co-construire » solo se pasa en ese modo,
  // así que lo usamos como señal para mostrar los textos sueltos en francés.
  const isAideProf = !!onWorkWithContent;
  const [tab, setTab] = useState<ViewerTab>('nomade');

  // ── Estado pestaña Nomade ──
  // Filtro por nivel (reunión 2026-07-21): el estudiante ve solo las temporadas
  // de su nivel y anteriores; el profesor ve todas las habilitadas (hasta la 4).
  const visibleSeasons = useMemo(
    () => (isTeacher ? NMD_SEASONS_AVAILABLE : nmdSeasonsForStudent(mcerLevel)),
    [isTeacher, mcerLevel],
  );
  const [season, setSeason] = useState<NmdSeason>(() => defaultSeasonFor(mcerLevel, visibleSeasons));
  const firstEpisode = season.episodes.find((e) => e.geniallyId);
  const [episode, setEpisode] = useState<NmdEpisode | undefined>(firstEpisode);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // ── Estado pestaña Cours internes (Mon alter ego) ──
  const [book, setBook] = useState<MaeBook>(() => defaultBookFor(mcerLevel));
  // URLs prefirmadas (key → URL temporal) obtenidas de /api/cours/sign. El bucket
  // es privado: cada recurso necesita una URL firmada de corta duración.
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [resLoading, setResLoading] = useState(false);
  const [resError, setResError] = useState<string | null>(null);

  // Recursos del libro filtrados por rol: el estudiante NO ve material docente
  // (guías, tests, corrigés). Se descartan los grupos que quedan vacíos.
  const bookResourceGroups = useMemo(() => {
    const groups = MAE_RESOURCES[book.number] ?? [];
    return groups
      .map((g) => ({ ...g, items: g.items.filter((it) => isTeacher || !it.teacherOnly) }))
      .filter((g) => g.items.length > 0);
  }, [book, isTeacher]);

  // Al abrir Cours internes o cambiar de libro, firmar las URLs de sus recursos
  // (solo los visibles para este rol).
  useEffect(() => {
    if (!showCoursInternes || tab !== 'cours') return;
    const keys = bookResourceGroups.flatMap((g) => g.items.map((i) => i.key)).filter((k) => !signedUrls[k]);
    if (keys.length === 0) return;
    let cancelled = false;
    (async () => {
      setResLoading(true);
      setResError(null);
      try {
        const cfg = configService.getConfig();
        // Token Galia (mismo que usa el WebSocket) — exigido por /api/cours/sign.
        // El backend da role=teacher SOLO con sesión LTI firmada + dominio
        // institucional (fase 4 del plan de seguridad LTI), por eso se adjunta
        // el token de sesión.
        const sessionToken = sessionService.getSessionToken();
        const tokRes = await fetch('/api/ws/generate-token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
          },
          body: JSON.stringify({
            email: cfg.email || '',
            fullName: cfg.userFullName || '',
            firstname: cfg.userFirstname || '',
            lastname: cfg.userLastname || '',
          }),
        });
        const tok = await tokRes.json();
        if (!tok.wsToken) throw new Error('no token');
        const signRes = await fetch('/api/cours/sign', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok.wsToken}` },
          body: JSON.stringify({ keys }),
        });
        if (!signRes.ok) throw new Error('sign ' + signRes.status);
        const data = await signRes.json();
        if (!cancelled && data.urls) setSignedUrls((prev) => ({ ...prev, ...data.urls }));
      } catch {
        if (!cancelled) setResError(isAideProf
          ? "Impossible de préparer les ressources. Rouvre l'onglet pour réessayer."
          : 'No se pudieron preparar los recursos. Reabre la pestaña para reintentar.');
      } finally {
        if (!cancelled) setResLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // signedUrls se omite a propósito (se acumula; incluirlo crearía un bucle).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCoursInternes, tab, bookResourceGroups]);

  const selectSeason = (s: NmdSeason) => {
    setSeason(s);
    const ep = s.episodes.find((e) => e.geniallyId);
    setEpisode(ep);
    setIframeLoaded(false);
  };

  const selectEpisode = (e: NmdEpisode) => {
    if (!e.geniallyId || e.geniallyId === episode?.geniallyId) return;
    setEpisode(e);
    setIframeLoaded(false);
  };

  // Episodios agrupados respetando el orden de aparición de los grupos
  const groups = useMemo(() => {
    const map = new Map<string, NmdEpisode[]>();
    for (const ep of season.episodes) {
      if (!ep.geniallyId) continue;
      const key = ep.group ?? '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ep);
    }
    return Array.from(map.entries());
  }, [season]);

  const url = episode?.geniallyId ? geniallyViewUrl(episode.geniallyId) : undefined;

  // ── Co-construcción (solo profesor) ──
  // Lleva al chat el contenido que el profesor está viendo, como un mensaje, para
  // que Galia (modo aide-prof) proponga actividades a partir de ese episodio/libro.
  const buildNomadeMessage = (): string => {
    if (!episode) return '';
    const epLine = [episode.group, episode.title].filter(Boolean).join(' — ');
    const sub = episode.subtitle ? ` (${episode.subtitle})` : '';
    const link = episode.geniallyId ? geniallyViewUrl(episode.geniallyId) : '';
    return [
      'Travaillons ensemble sur ce contenu du parcours « Nomade » que je suis en train de voir :',
      `- Saison S${season.number} · ${season.name} (${season.afEquivalence})`,
      `- Épisode : ${epLine}${sub}`,
      link ? `- Genially : ${link}` : '',
      '',
      'Propose 2 ou 3 activités avec Galia pour mes élèves à partir de cet épisode (préparation, pendant ou prolongement). Si besoin, demande-moi le niveau du groupe.',
    ].filter(Boolean).join('\n');
  };

  // Co-construcción por OBJETIVO (pedido de Santiago): en lugar de mandar el libro
  // entero (nivel demasiado amplio), el profesor elige un objetivo puntual del
  // módulo y Galia propone actividades centradas en ese objetivo.
  const buildObjectiveMessage = (m: MaeBook['modules'][number], objective: string): string => {
    return [
      'Travaillons ensemble sur cet objectif des Cours internes (méthode « Mon alter ego ») que je suis en train de voir :',
      `- Livre : ${book.title} (${book.levelRange})`,
      `- Module : ${m.module} (${m.dossiers}) — niveau ${m.level}`,
      `- Objectif : « ${objective} »`,
      '',
      'Propose 2 ou 3 activités avec Galia centrées sur cet objectif (préparation, pendant ou prolongement). Si besoin, demande-moi le niveau du groupe et le temps disponible.',
    ].join('\n');
  };

  const handleCoConstruct = () => {
    // Solo Nomade: en Cours internes la co-construcción es por objetivo (más abajo).
    if (!onWorkWithContent) return;
    const msg = buildNomadeMessage();
    if (!msg) return;
    onWorkWithContent(msg);
  };

  const handleObjectiveCoConstruct = (m: MaeBook['modules'][number], objective: string) => {
    if (!onWorkWithContent) return;
    onWorkWithContent(buildObjectiveMessage(m, objective));
  };

  const coConstructIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M12 7v6"/><path d="M9 10h6"/></svg>
  );

  return (
    <div
      className={inline ? 'content-viewer-inline' : 'content-viewer-overlay'}
      onClick={inline ? undefined : (e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="content-viewer-panel"
        role={inline ? 'region' : 'dialog'}
        aria-label="Contenus du cours"
      >
        {/* En columna no hay cabecera propia: el layout ya muestra las
            pestañas arriba y el contenido no se cierra. */}
        {!inline && (
          <header className="content-viewer-header">
            <div className="content-viewer-title">
              <h2>Contenus du cours{!showCoursInternes ? ' — Nomade' : ''}</h2>
              <span className="content-viewer-subtitle">Alliance Française</span>
            </div>
            <button className="content-viewer-close" onClick={onClose} aria-label={isAideProf ? 'Fermer' : 'Cerrar'}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </header>
        )}

        {/* Pestañas Nomade / Cours internes — solo en modo profesor */}
        {showCoursInternes && (
          <div className="content-viewer-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'nomade'}
              className={`content-viewer-tab ${tab === 'nomade' ? 'active' : ''}`}
              onClick={() => setTab('nomade')}
            >
              Nomade
            </button>
            <button
              role="tab"
              aria-selected={tab === 'cours'}
              className={`content-viewer-tab ${tab === 'cours' ? 'active' : ''}`}
              onClick={() => setTab('cours')}
            >
              Cours internes
            </button>
          </div>
        )}

        {tab === 'nomade' ? (
          <>
            <div className="content-viewer-seasons" role="tablist">
              {visibleSeasons.map((s) => (
                <button
                  key={s.number}
                  role="tab"
                  aria-selected={s.number === season.number}
                  className={`content-season-chip ${s.number === season.number ? 'active' : ''}`}
                  onClick={() => selectSeason(s)}
                  title={s.synopsis}
                >
                  <span className="content-season-level">{s.afEquivalence}</span>
                  <span className="content-season-name">S{s.number} · {s.name}</span>
                </button>
              ))}
            </div>

            <div className="content-viewer-body">
              <nav className="content-episode-list" aria-label="Épisodes">
                {groups.map(([group, eps]) => (
                  <div key={group || 'default'} className="content-episode-group">
                    {group && <div className="content-episode-group-title">{group}</div>}
                    {eps.map((ep) => (
                      <button
                        key={`${ep.geniallyId}-${ep.title}`}
                        className={`content-episode-item ${ep.geniallyId === episode?.geniallyId && ep.title === episode?.title ? 'active' : ''}`}
                        onClick={() => selectEpisode(ep)}
                      >
                        <span className="content-episode-title">{ep.title}</span>
                        {ep.subtitle && <span className="content-episode-subtitle">{ep.subtitle}</span>}
                      </button>
                    ))}
                  </div>
                ))}
              </nav>

              <div className="content-viewer-stage">
                {url ? (
                  <>
                    {!iframeLoaded && (
                      <div className="content-viewer-loading">
                        <div className="content-viewer-spinner" />
                        <p>Chargement de l'épisode…</p>
                      </div>
                    )}
                    <iframe
                      key={url}
                      src={url}
                      title={episode?.title || 'Épisode Nomade'}
                      allow="fullscreen; autoplay"
                      allowFullScreen
                      onLoad={() => setIframeLoaded(true)}
                    />
                    <div className="content-viewer-stage-footer">
                      <span className="content-viewer-current">{episode?.title}</span>
                      <div className="content-viewer-stage-actions">
                        {onWorkWithContent && (
                          <button type="button" className="content-viewer-coconstruct" onClick={handleCoConstruct}>
                            {coConstructIcon}
                            Co-construire avec Galia
                          </button>
                        )}
                        <a href={url} target="_blank" rel="noopener noreferrer" className="content-viewer-open-tab">
                          Ouvrir dans un nouvel onglet
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        </a>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="content-viewer-empty">
                    <p>Cette saison n'a pas encore d'épisodes publiés.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="content-viewer-seasons" role="tablist">
              {MAE_BOOKS.map((b) => (
                <button
                  key={b.number}
                  role="tab"
                  aria-selected={b.number === book.number}
                  className={`content-season-chip ${b.number === book.number ? 'active' : ''}`}
                  onClick={() => setBook(b)}
                  title={b.levelRange}
                >
                  <span className="content-season-level">{b.levelRange.split(' (')[0]}</span>
                  <span className="content-season-name">{b.title}</span>
                </button>
              ))}
            </div>

            <div className="content-viewer-body content-viewer-body--cours">
              <div className="content-cours-pane">
                <div className="content-cours-head">
                  <div className="content-cours-head-main">
                    <h3 className="content-cours-title">{book.title}</h3>
                    <span className="content-cours-levels">{book.levelRange}</span>
                  </div>
                  {onWorkWithContent && (
                    <p className="content-cours-coconstruct-hint">
                      {coConstructIcon}
                      Choisis un objectif ci-dessous pour co-construire une activité avec Galia.
                    </p>
                  )}
                  {book.note && <p className="content-cours-note">⚠️ {book.note}</p>}
                </div>

                <div className="content-cours-modules">
                  {book.modules.map((m) => (
                    <div key={m.module} className="content-cours-module">
                      <div className="content-cours-module-head">
                        <span className="content-cours-module-code">{m.module}</span>
                        <span className="content-cours-module-meta">{m.dossiers} · p. {m.pages}</span>
                      </div>
                      <ul className="content-cours-objectives">
                        {m.objectives.map((o, i) => (
                          <li key={i} className="content-cours-objective">
                            <span className="content-cours-objective-text">{o}</span>
                            {onWorkWithContent && (
                              <button
                                type="button"
                                className="content-cours-obj-coconstruct"
                                onClick={() => handleObjectiveCoConstruct(m, o)}
                                title="Co-construire une activité avec Galia sur cet objectif"
                              >
                                {coConstructIcon}
                                <span>Co-construire</span>
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {bookResourceGroups.length > 0 && (
                  <div className="content-cours-resources">
                    <h4 className="content-cours-res-heading">
                      Ressources du livre
                      {resLoading && <span className="content-cours-res-note"> · préparation…</span>}
                    </h4>
                    {resError && <p className="content-cours-res-error">{resError}</p>}
                    {bookResourceGroups.map((group) => (
                      <div key={group.title} className="content-cours-resgroup">
                        <div className="content-cours-resgroup-title">{group.title}</div>
                        <div className="content-cours-reslist">
                          {group.items.map((it) => {
                            const icon = it.kind === 'audio' ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            );
                            const href = signedUrls[it.key];
                            return href ? (
                              <a
                                key={it.key}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`content-cours-reslink content-cours-reslink--${it.kind}`}
                              >
                                {icon}
                                <span>{it.label}</span>
                              </a>
                            ) : (
                              <span
                                key={it.key}
                                className={`content-cours-reslink content-cours-reslink--${it.kind} content-cours-reslink--pending`}
                                aria-disabled="true"
                                title="Préparation du lien…"
                              >
                                {icon}
                                <span>{it.label}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <p className="content-cours-footnote">
                  Méthode « Mon alter ego » — Cours présentiels (SUPER / INTENSIF / SEMI). Material de
                  planeación para el profesor. Los archivos (Classe+, guides, tests) se abren en una
                  pestaña nueva.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
